import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { LandmarkPoint } from '~/domain/landmark/landmark';
import { useSelectedRegistrationFrames } from '~/hooks/viewer/useSelectedDeformationField';
import { useLandmarkController } from '~/hooks/useLandmarkController';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useOpenLandmarkComparison } from '~/hooks/viewer/useOpenLandmarkComparison';
import { enqueueSnackbar } from '~/utils/notifications';

export function useLandmarkPlacement(): (point: LandmarkPoint) => void {
    const intl = useIntl();
    const { addPoint } = useLandmarkController();
    const frames = useSelectedRegistrationFrames();
    const patient = useSelectedPatient();
    const openComparison = useOpenLandmarkComparison();

    return useCallback(
        (point: LandmarkPoint) => {
            if (!frames) {
                refuse(intl.formatMessage({ id: 'landmark.refused.noField' }));
                return;
            }
            // A frame registered to itself names no second scan, so the pair could never complete.
            if (frames.fixed === frames.moving) {
                refuse(intl.formatMessage({ id: 'landmark.refused.oneFrame' }));
                return;
            }
            const image = patient?.images.find(
                (candidate) =>
                    candidate.seriesUid === point.plane.seriesInstanceUid
            );
            // An unknown series may be the registration's own output; a point on it measures nothing.
            if (!image || image.isSpatiallyResampled) {
                refuse(
                    intl.formatMessage({ id: 'landmark.refused.derivedImage' })
                );
                return;
            }
            if (point.frameOfReferenceUid === frames.fixed) {
                addPoint(point, false);
                openComparison();
                return;
            }
            if (point.frameOfReferenceUid === frames.moving) {
                addPoint(point, true);
                return;
            }
            refuse(
                intl.formatMessage({ id: 'landmark.refused.unrelatedFrame' })
            );
        },
        [intl, addPoint, frames, patient, openComparison]
    );
}

function refuse(message: string): void {
    enqueueSnackbar(message, { variant: 'warning', preventDuplicate: true });
}
